import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fmtUSD, fmtDate } from "../lib/format";
import { Plus, Pencil, Trash2, ArrowUpRight, Calendar } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "chime", l: "Chime" },
  { v: "cashapp", l: "CashApp" },
  { v: "paypal", l: "PayPal" },
  { v: "venmo", l: "Venmo" },
  { v: "link", l: "Link" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
};

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

export default function Withdrawals() {
  const [txns, setTxns] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    wallet_type: "chime",
    wallet_id: "",
    amount_usd: "",
    rate: "",
    notes: "",
  });
  const [filter, setFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const dateRange = useMemo(() => {
    if (datePreset === "today") return { from: todayISO(), to: todayISO() };
    if (datePreset === "month") return { from: monthStartISO(), to: todayISO() };
    if (datePreset === "custom" && (from || to)) return { from, to };
    return {};
  }, [datePreset, from, to]);

  const load = async () => {
    setLoading(true);
    try {
      const [t, w, r] = await Promise.all([
        api.get("/transactions", { params: { kind: "withdrawal", limit: 5000 } }),
        api.get("/wallets"),
        api.get("/rates"),
      ]);
      setTxns(t.data);
      setWallets(w.data);
      const rm = {};
      r.data.forEach((x) => (rm[x.wallet_type] = x.rate));
      setRates(rm);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const walletsOfType = useMemo(
    () => wallets.filter((w) => w.type === form.wallet_type),
    [wallets, form.wallet_type]
  );

  // Auto-fill rate when type changes
  useEffect(() => {
    if (editing) return;
    setForm((f) => ({ ...f, rate: rates[f.wallet_type] ?? 1 }));
  }, [editing, rates, form.wallet_type]);

  const filtered = useMemo(() => {
    return txns.filter((t) => {
      if (filter !== "all" && t.wallet_type !== filter) return false;
      const d = (t.created_at || "").slice(0, 10);
      if (dateRange.from && d < dateRange.from) return false;
      if (dateRange.to && d > dateRange.to) return false;
      return true;
    });
  }, [txns, filter, dateRange.from, dateRange.to]);

  const dateLabel = (() => {
    if (datePreset === "all") return "All time";
    if (datePreset === "today") return `Today · ${dateRange.from}`;
    if (datePreset === "month") return `Month · ${dateRange.from} → ${dateRange.to}`;
    if (dateRange.from && dateRange.to)
      return `${dateRange.from} → ${dateRange.to}`;
    if (dateRange.from) return `From ${dateRange.from}`;
    if (dateRange.to) return `Until ${dateRange.to}`;
    return "Custom";
  })();

  const totalUSD = filtered.reduce((s, t) => s + (t.amount_usd || 0), 0);
  const totalUSDT = filtered.reduce((s, t) => s + (t.amount_usdt || 0), 0);

  const openCreate = () => {
    setEditing(null);
    setForm({
      wallet_type: "chime",
      wallet_id: "",
      amount_usd: "",
      rate: rates.chime ?? 1,
      notes: "",
    });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      wallet_type: t.wallet_type || "chime",
      wallet_id: t.wallet_id || "",
      amount_usd: String(t.amount_usd ?? ""),
      rate: t.rate ?? rates[t.wallet_type] ?? 1,
      notes: t.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (!form.wallet_id) {
        toast.error("Select a wallet");
        return;
      }
      const payload = {
        wallet_id: form.wallet_id,
        amount_usd: Number(form.amount_usd),
        rate: form.rate ? Number(form.rate) : null,
        notes: form.notes,
      };
      if (editing) {
        await api.put(`/transactions/${editing.id}`, payload);
        toast.success("Withdrawal updated");
      } else {
        await api.post("/transactions", {
          ...payload,
          kind: "withdrawal",
        });
        toast.success("Withdrawal recorded");
      }
      setOpen(false);
      setEditing(null);
      setForm({
        wallet_type: "chime",
        wallet_id: "",
        amount_usd: "",
        rate: rates.chime ?? 1,
        notes: "",
      });
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this withdrawal? Balance will be restored."))
      return;
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  return (
    <Layout
      title="Withdrawals"
      subtitle="Record USD → USDT withdrawals across all wallets"
      testId="withdrawals-page"
      actions={
        <Button
          onClick={openCreate}
          className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
          data-testid="add-withdrawal-btn"
        >
          <Plus size={14} className="mr-1.5" /> New withdrawal
        </Button>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card p-5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">
            Total (filter)
          </div>
          <div className="font-mono text-[26px] font-medium mt-2">
            {fmtUSD(totalUSD)}
          </div>
        </div>
        <div className="stat-card p-5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">
            USDT received
          </div>
          <div className="font-mono text-[26px] font-medium mt-2">
            {totalUSDT.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
          </div>
        </div>
        <div className="stat-card p-5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">
            Entries
          </div>
          <div className="font-mono text-[26px] font-medium mt-2">
            {filtered.length}
          </div>
        </div>
      </div>

      <div className="stat-card p-3 mb-3 flex flex-wrap items-center gap-2">
        <Calendar size={13} strokeWidth={1.7} className="text-zinc-500 ml-1" />
        <span className="text-[11.5px] uppercase tracking-widest text-zinc-500 mr-1">
          Period
        </span>
        {DATE_PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => setDatePreset(p.id)}
            data-testid={`wd-date-${p.id}`}
            className={`h-8 px-3 rounded-md text-[12.5px] border transition-colors ${
              datePreset === p.id
                ? "bg-zinc-900 text-white border-zinc-900"
                : "hairline bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {p.label}
          </button>
        ))}
        {datePreset === "custom" && (
          <div className="flex items-center gap-2 ml-1">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8 w-[150px]"
              data-testid="wd-date-from"
            />
            <span className="text-zinc-400 text-xs">→</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 w-[150px]"
              data-testid="wd-date-to"
            />
          </div>
        )}
        <div className="flex items-center gap-2 text-[12.5px] ml-auto">
          <span className="text-zinc-500">Provider</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-36" data-testid="withdrawals-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All providers</SelectItem>
              {TYPES.map((t) => (
                <SelectItem key={t.v} value={t.v}>
                  {t.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span
          className="basis-full text-[11.5px] text-zinc-500 font-mono mt-1 ml-1"
          data-testid="wd-filter-label"
        >
          Showing {dateLabel}
        </span>
      </div>

      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-5 font-medium">When</th>
                <th className="py-3 px-5 font-medium">Wallet</th>
                <th className="py-3 px-5 font-medium">Provider</th>
                <th className="py-3 px-5 font-medium text-right">USD</th>
                <th className="py-3 px-5 font-medium text-right">Rate</th>
                <th className="py-3 px-5 font-medium text-right">USDT</th>
                <th className="py-3 px-5 font-medium">Notes</th>
                <th className="py-3 px-5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-zinc-500">
                    No withdrawals recorded.
                  </td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr key={t.id} className="row-hover" data-testid={`wd-row-${t.id}`}>
                    <td className="py-3 px-5 font-mono text-[12px] text-zinc-600">
                      {fmtDate(t.created_at)}
                    </td>
                    <td className="py-3 px-5">{t.wallet_name}</td>
                    <td className="py-3 px-5 text-zinc-600 capitalize">
                      {t.wallet_type}
                    </td>
                    <td className="py-3 px-5 font-mono text-right">
                      {fmtUSD(t.amount_usd)}
                    </td>
                    <td className="py-3 px-5 font-mono text-right">
                      {t.rate?.toFixed(4)}
                    </td>
                    <td className="py-3 px-5 font-mono text-right text-emerald-700">
                      {t.amount_usdt?.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 4,
                      })}
                    </td>
                    <td className="py-3 px-5 text-[12.5px] text-zinc-500 truncate max-w-[200px]">
                      {t.notes || "—"}
                    </td>
                    <td className="py-3 px-5 text-right">
                      <button
                        onClick={() => openEdit(t)}
                        className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                        title="Edit withdrawal"
                        data-testid={`edit-wd-${t.id}`}
                      >
                        <Pencil size={14} strokeWidth={1.6} />
                      </button>
                      <button
                        onClick={() => remove(t)}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                        data-testid={`delete-wd-${t.id}`}
                      >
                        <Trash2 size={14} strokeWidth={1.6} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditing(null);
        }}
      >
        <DialogContent data-testid="withdrawal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit withdrawal" : "New withdrawal"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Updates the withdrawal and adjusts wallet balances automatically."
                : "Deducts USD from the wallet and records USDT using the rate."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select
                value={form.wallet_type}
                onValueChange={(v) =>
                  setForm({ ...form, wallet_type: v, wallet_id: "", rate: rates[v] ?? 1 })
                }
              >
                <SelectTrigger data-testid="wd-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>
                      {t.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Wallet</Label>
              <Select
                value={form.wallet_id}
                onValueChange={(v) => setForm({ ...form, wallet_id: v })}
              >
                <SelectTrigger data-testid="wd-wallet-select">
                  <SelectValue placeholder="Select wallet" />
                </SelectTrigger>
                <SelectContent>
                  {walletsOfType.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} — {fmtUSD(w.balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount (USD) *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                data-testid="wd-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rate (USD → USDT)</Label>
              <Input
                type="number"
                step="0.0001"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                data-testid="wd-rate-input"
              />
              <div className="text-[11px] text-zinc-500 font-mono">
                ≈ {fmtUSD(Number(form.amount_usd || 0))} →{" "}
                {(Number(form.amount_usd || 0) * Number(form.rate || 0)).toFixed(4)} USDT
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional"
                data-testid="wd-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="submit-withdrawal-btn"
            >
              <ArrowUpRight size={14} className="mr-1" />
              {editing ? "Save withdrawal" : "Record withdrawal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
